import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ServiceModal from './ServiceModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { ServiceType } from "../../../../types/ServiceType";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const Services: React.FC = () => {
  const [services, setServices] = useState<ServiceType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<ServiceType | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchServices = async () => {
    try {
      const data = await apiRequest<ServiceType[]>({
        method: 'get',
        url: 'get-services',
      });
      setServices(data);
    } catch (error) {
      showError('Failed to fetch service');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleEditClick = (record: ServiceType) => {
    setIsEditMode(true);
    setInitialValues({
      service_id: record.service_id,
      service_name: record.service_name,
      service_status: record.service_status,
      service_image: record.service_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchServices();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (service_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-service/${service_id}`,
      });
      showSuccess('service deleted');
      fetchServices();
    } catch {
      showError('Failed to delete service');
    }
  };

  const handleStatusToggle = async (serviceID: number) => {
    setServices((prev) =>
      prev.map((item) =>
        item.service_id === serviceID
          ? { ...item, service_status: item.service_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-service/${serviceID}`,
      });
      showSuccess('service status updated');
    } catch (error) {
      showError('Failed to update status');
      setServices((prev) =>
        prev.map((item) =>
          item.service_id === serviceID
            ? { ...item, service_status: item.service_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<ServiceType>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<ServiceType> = [
    
    {
      title: 'Image',
      dataIndex: 'service_image',
      key: 'service_image',
      width: 150,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.service_image}
            alt="service"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    },
    
    {
      title: 'Title',
      dataIndex: 'service_name',
      key: 'service_name',
      filters: Array.from(new Set(services.map((s) => s.service_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.service_name || null,
      onFilter: (value, record) => record.service_name.includes(value as string),
      sorter: (a, b) => a.service_name.localeCompare(b.service_name),
      sortOrder: sortedInfo.columnKey === 'service_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div className='text-wrap'>{record.service_name}</div>,
    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 150,
      fixed: 'right',
      render: (_: any, record: ServiceType) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.service_status}
                onChange={() => handleStatusToggle(record.service_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this service?"
              onConfirm={() => handleDelete(record.service_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Service</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Service 
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<ServiceType>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="service_id"
          dataSource={services.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          // scroll={{ y: 37 * 13 }}
           scroll={{ x: 1400, y: 37 * 17 }}
          locale={{ emptyText: 'No service found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, services.length)} of{' '}
                {services.length}
              </span>
              <Pagination
                {...pagination}
                total={services.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ServiceModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        services={services}
      />
    </>
  );
};

export default Services;